import React, { useEffect, useState } from 'react';
import {
  Drawer,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Button,
  Upload,
  message,
} from 'antd';
import { UploadOutlined, PictureOutlined } from '@ant-design/icons';
import { uploadDishCoverImage } from '../../../services/uploadService';
import type {
  MenuDish,
  MenuCategory,
  DishStatus,
  StockStatus,
} from '../../../types/menu';
import { formatVnDateTime } from '../../../utils/vnDateTime';

const COVER_PREVIEW_SIZE = 120;

export interface EditDishDrawerProps {
  open: boolean;
  dish: MenuDish | null;
  categories: MenuCategory[];
  onClose: () => void;
  onSave: (
    dishId: string,
    values: Partial<MenuDish> & { coverImage?: string | null },
    adminNote: string
  ) => Promise<void>;
  onOpenAdminNoteModal: (
    title: string,
    onConfirm: (note: string) => Promise<void>
  ) => void;
}

/** Get display image URL from dish (thumbnailUrl or legacy cover_image/image) */
function getDishImageUrl(d: MenuDish | null): string | null {
  if (!d) return null;
  if (d.thumbnailUrl) return d.thumbnailUrl;
  const anyDish = d as MenuDish & { cover_image?: string; image?: string };
  return anyDish.cover_image || anyDish.image || null;
}

export const EditDishDrawer: React.FC<EditDishDrawerProps> = ({
  open,
  dish,
  categories,
  onClose,
  onSave,
  onOpenAdminNoteModal,
}) => {
  const [form] = Form.useForm();
  const [uploadingCover, setUploadingCover] = useState(false);
  const [coverPreviewUrl, setCoverPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    if (open && dish) {
      const initialCover = getDishImageUrl(dish) || undefined;
      setCoverPreviewUrl(null);
      form.setFieldsValue({
        name: dish.name,
        description: dish.description ?? '',
        price: dish.price,
        discountAmount: dish.discountAmount ?? undefined,
        discountPercent: dish.discountPercent ?? undefined,
        status: dish.status,
        stockStatus: dish.stockStatus,
        categoryId: dish.categoryId ?? undefined,
        coverImage: initialCover,
      });
    }
  }, [open, dish, form]);

  const handleCoverUpload = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      message.error('Chỉ chấp nhận file hình ảnh (jpg, png, gif, webp)');
      return;
    }
    try {
      setUploadingCover(true);
      const { url, signedUrl } = await uploadDishCoverImage(file);
      form.setFieldsValue({ coverImage: url });
      setCoverPreviewUrl(signedUrl);
      message.success('Upload ảnh bìa thành công');
    } catch (err: unknown) {
      const e = err as {
        response?: { data?: { message?: string } };
        message?: string;
      };
      message.error(
        e.response?.data?.message || e.message || 'Upload ảnh thất bại'
      );
    } finally {
      setUploadingCover(false);
    }
  };

  const handleSave = () => {
    form.validateFields().then(values => {
      if (!dish) return;
      onOpenAdminNoteModal('Confirm dish changes', async note => {
        await onSave(
          dish.id,
          {
            name: values.name,
            description: values.description || null,
            price: values.price,
            discountAmount: values.discountAmount ?? null,
            discountPercent: values.discountPercent ?? null,
            status: values.status,
            stockStatus: values.stockStatus,
            categoryId: values.categoryId ?? null,
            coverImage: values.coverImage?.trim() || null,
          },
          note
        );
        onClose();
      });
    });
  };

  return (
    <Drawer
      title="Edit Dish"
      placement="right"
      width={420}
      open={open}
      onClose={onClose}
      destroyOnClose
      extra={
        <Space>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" onClick={handleSave}>
            Save
          </Button>
        </Space>
      }
    >
      {dish && (
        <>
          {dish.lastEditedBy && (
            <div
              className="merchant-menu-drawer-meta"
              style={{ marginBottom: 16 }}
            >
              Last edited by {dish.lastEditedBy}
              {dish.lastEditedAt && (
                <> at {formatVnDateTime(dish.lastEditedAt)}</>
              )}
            </div>
          )}
          <Form form={form} layout="vertical">
            <Form.Item name="coverImage" label="Ảnh bìa món">
              <Form.Item noStyle shouldUpdate>
                {({ getFieldValue, setFieldsValue }) => {
                  const coverUrl = getFieldValue('coverImage');
                  const displayUrl =
                    coverPreviewUrl ?? coverUrl ?? getDishImageUrl(dish);
                  return (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 12,
                      }}
                    >
                      {displayUrl ? (
                        <div style={{ flexShrink: 0 }}>
                          <img
                            src={displayUrl}
                            alt={dish.name}
                            style={{
                              width: COVER_PREVIEW_SIZE,
                              height: COVER_PREVIEW_SIZE,
                              objectFit: 'cover',
                              borderRadius: 8,
                              border: '1px solid #d9d9d9',
                            }}
                          />
                        </div>
                      ) : (
                        <div
                          style={{
                            width: COVER_PREVIEW_SIZE,
                            height: COVER_PREVIEW_SIZE,
                            border: '1px dashed #d9d9d9',
                            borderRadius: 8,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: '#fafafa',
                          }}
                        >
                          <PictureOutlined
                            style={{ fontSize: 28, color: '#bfbfbf' }}
                          />
                        </div>
                      )}
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 8,
                        }}
                      >
                        <Upload
                          accept="image/jpeg,image/png,image/gif,image/webp"
                          showUploadList={false}
                          beforeUpload={f => {
                            handleCoverUpload(f);
                            return false;
                          }}
                          disabled={uploadingCover}
                        >
                          <Button
                            icon={<UploadOutlined />}
                            loading={uploadingCover}
                          >
                            {displayUrl ? 'Thay ảnh' : 'Chọn ảnh (tùy chọn)'}
                          </Button>
                        </Upload>
                        {displayUrl && (
                          <Button
                            type="text"
                            danger
                            size="small"
                            onClick={() => {
                              setFieldsValue({ coverImage: undefined });
                              setCoverPreviewUrl(null);
                            }}
                          >
                            Xóa ảnh
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                }}
              </Form.Item>
            </Form.Item>
            <Form.Item
              name="name"
              label="Tên món"
              rules={[{ required: true, message: 'Nhập tên món' }]}
            >
              <Input placeholder="Ví dụ: Phở bò" />
            </Form.Item>
            <Form.Item name="description" label="Mô tả">
              <Input.TextArea rows={3} placeholder="Mô tả ngắn (tùy chọn)" />
            </Form.Item>
            <Form.Item
              name="price"
              label="Giá (VNĐ)"
              rules={[
                { required: true, message: 'Nhập giá' },
                {
                  validator: (_, value) => {
                    const num =
                      typeof value === 'number'
                        ? value
                        : Number(String(value ?? '').replace(/,/g, ''));
                    if (Number.isNaN(num) || num <= 0) {
                      return Promise.reject(new Error('Giá phải lớn hơn 0'));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <InputNumber
                style={{ width: '100%' }}
                min={1}
                placeholder="45000"
              />
            </Form.Item>
            <Form.Item name="discountAmount" label="Giảm giá (VNĐ)">
              <InputNumber style={{ width: '100%' }} min={0} />
            </Form.Item>
            <Form.Item name="discountPercent" label="Giảm giá (%)">
              <InputNumber style={{ width: '100%' }} min={0} max={100} />
            </Form.Item>
            <Form.Item name="categoryId" label="Danh mục">
              <Select
                placeholder="Chọn danh mục (tùy chọn)"
                allowClear
                options={categories.map(c => ({
                  value: c.id,
                  label: c.name,
                }))}
              />
            </Form.Item>
            <Form.Item name="status" label="Trạng thái">
              <Select<DishStatus>
                options={[
                  { value: 'PUBLISHED', label: 'Published' },
                  { value: 'HIDDEN', label: 'Hidden' },
                ]}
              />
            </Form.Item>
            <Form.Item name="stockStatus" label="Kho">
              <Select<StockStatus>
                options={[
                  { value: 'IN_STOCK', label: 'In stock' },
                  { value: 'OUT_OF_STOCK', label: 'Out of stock' },
                ]}
              />
            </Form.Item>
          </Form>
        </>
      )}
    </Drawer>
  );
};
